import { formatStatusDate } from "@components/utils/formatStatusDate";

export const NEEDS_ATTENTION_FILTER_LABEL = "Needs attention";

export const INVITE_FILTER_GROUP = {
  label: "Invites",
  items: [
    { label: "No Filter", value: "no_filter" },
    { label: NEEDS_ATTENTION_FILTER_LABEL, value: "needs_attention" },
  ],
};

const STAGE_KEY_BY_LABEL = {
  sending: "sending",
  sent: "sent",
  delivered: "delivered",
  "link clicked": "link_clicked",
  "reminder 1 of 2 sent": "reminder_1_sent",
  "final reminder sent": "final_reminder_sent",
  "link expired": "link_expired",
  stalled: "stalled",
  bounced: "bounced",
  failed: "failed",
};

const NON_ACCEPTED_STATES = ["pending", "expired", "delivery failed"];

const sentence = (template, values) => {
  const keys = template.match(/\{(\w+)\}/g) || [];
  const missing = keys.some((k) => !values[k.slice(1, -1)]);
  if (missing) return "";
  return template.replace(/\{(\w+)\}/g, (_, key) => values[key]);
};

const STAGE_COPY = {
  sending: {
    title: "Preparing invite",
    sentences: ["The invitation is being sent to {email}."],
  },
  sent: {
    title: "Invite sent",
    sentences: [
      ["Sent {time} by {inviter}.", "Sent {time}."],
      "Delivery not yet confirmed.",
      "Next reminder {reminder}.",
    ],
  },
  delivered: {
    title: "Invite delivered",
    sentences: [
      "Reached the inbox {time}.",
      "Waiting for {name} to sign up.",
      "Next reminder {reminder}.",
    ],
  },
  link_clicked: {
    title: "Link clicked",
    sentences: [
      "Opened {time} but sign-up isn't finished.",
      "No action needed.",
      "Next reminder {reminder}.",
    ],
  },
  reminder_1_sent: {
    title: "Reminder 1 of 2 sent",
    sentences: [
      "Automatic reminder sent {time}.",
      "Link valid until {expiry}.",
      "Next reminder {reminder}.",
    ],
  },
  final_reminder_sent: {
    title: "Final reminder sent",
    sentences: [
      "No more automatic reminders.",
      "If not accepted by {expiry}, the invite shows Stalled.",
    ],
  },
  link_expired: {
    title: "Invite link expired",
    sentences: ["Expired {expiry} before sign-up.", "Resend to issue a new 7-day link."],
  },
  stalled: {
    title: "Invite stalled",
    sentences: [
      "Both reminders were sent and the link has expired.",
      "Nothing more happens automatically.",
      "Resend or cancel.",
    ],
  },
  bounced: {
    title: "Invite bounced",
    sentences: [
      "The email to {email} couldn't be delivered.",
      "Change the email, resend, or cancel.",
    ],
  },
  failed: {
    title: "Invite failed",
    sentences: [
      "The email service couldn't send the invite.",
      "Resend, or change the email if it keeps failing.",
    ],
  },
};

const ATTENTION_STAGES = ["bounced", "failed"];

const normalizeString = (value) => (value == null ? "" : String(value).trim().toLowerCase());

export function getInviteStageKey(stage) {
  const normalized = normalizeString(stage);
  if (!normalized) return "";
  if (Object.values(STAGE_KEY_BY_LABEL).includes(normalized)) return normalized;
  return STAGE_KEY_BY_LABEL[normalized.replace(/_/g, " ")] || "";
}

export function isInviteRow(access) {
  if (!access) return false;
  if (!NON_ACCEPTED_STATES.includes(normalizeString(access.invite_state))) return false;
  return normalizeString(access.user?.state) === "invited";
}

export function needsAttention(access) {
  return access?.action_required === true;
}

function formatFullName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(" ");
}

function getEventDate(access, stageKey) {
  if (stageKey === "sent") {
    return access.invite_sent_at || access.user?.last_invited_at;
  }
  if (stageKey === "delivered") return access.invite_delivered_at;
  return access.user?.last_invited_at;
}

export function getInviteTooltip(access) {
  const stageKey = getInviteStageKey(access?.invite_stage);
  const copy = STAGE_COPY[stageKey];
  if (!copy) return null;
  const values = {
    email: access.user?.email,
    name: formatFullName(access.user),
    inviter: access.invited_by_name,
    time: formatStatusDate(getEventDate(access, stageKey)),
    expiry: formatStatusDate(access.invite_expires_at),
    reminder: formatStatusDate(access.invite_next_reminder_at),
  };
  const description = copy.sentences
    .map((entry) => {
      const variants = Array.isArray(entry) ? entry : [entry];
      return variants.map((template) => sentence(template, values)).find(Boolean) || "";
    })
    .filter(Boolean)
    .join(" ");
  return { title: copy.title, description };
}

export function getInviteCellLines(access) {
  const stageKey = getInviteStageKey(access?.invite_stage);
  const state = normalizeString(access?.invite_state);
  const expiry = formatStatusDate(access?.invite_expires_at);
  let primary = "—";
  if (stageKey === "bounced") {
    primary = "Email bounced";
  } else if (expiry && state === "pending") {
    primary = `Expires ${expiry}`;
  } else if (expiry && state === "expired") {
    primary = `Expired ${expiry}`;
  }
  const invitedAt = formatStatusDate(access?.invite_sent_at || access?.user?.last_invited_at);
  const inviter = access?.invited_by_name;
  let secondary = "";
  if (inviter && invitedAt) secondary = `Invited by ${inviter} · ${invitedAt}`;
  else if (inviter) secondary = `Invited by ${inviter}`;
  else if (invitedAt) secondary = `Invited ${invitedAt}`;
  return { primary, secondary };
}

const INVITE_ITEM_VALUES = ["resend_invite", "cancel_invite_action", "change_email_action"];

const CHANGE_EMAIL_ITEM = {
  name: "Change Sign in email",
  value: "change_email_action",
  icon: "icons/dropdown/at_the_rate_sign.svg",
};

const RESEND_ITEM = {
  name: "Resend Invite",
  value: "resend_invite",
  icon: "icons/dropdown/resend.svg",
};

const CANCEL_ITEM = {
  name: "Cancel Invite",
  value: "cancel_invite_action",
  icon: "icons/dropdown/cancel-invite.svg",
  negative: true,
};

export function isOwnerOrCoOwner(permission) {
  const normalized = normalizeString(permission).replace(/[\s-]+/g, "");
  return normalized === "owner" || normalized === "coowner";
}

export function buildInviteRowMenuItems(baseItems, access, sessionPermission) {
  const items = Array.isArray(baseItems) ? baseItems : [];
  if (!isInviteRow(access)) return items;
  const existingChangeEmail = items.find((item) => item.value === "change_email_action");
  const rest = items.filter((item) => !INVITE_ITEM_VALUES.includes(item.value));
  if (!isOwnerOrCoOwner(sessionPermission)) return rest;
  const stageKey = getInviteStageKey(access.invite_stage);
  const lead = ATTENTION_STAGES.includes(stageKey)
    ? [existingChangeEmail || CHANGE_EMAIL_ITEM, RESEND_ITEM, CANCEL_ITEM]
    : [RESEND_ITEM, CANCEL_ITEM];
  if (!ATTENTION_STAGES.includes(stageKey) && existingChangeEmail) {
    return [...lead, ...rest, existingChangeEmail];
  }
  return [...lead, ...rest];
}

const CANCEL_EMAIL_CHANGE_ITEM = {
  name: "Cancel email change",
  value: "cancel_email_change_action",
  icon: "icons/dropdown/cancel-invite.svg",
  negative: true,
};

export const SUPERUSER_CHANGE_EMAIL_REASON = "Change Email unavailable";
export const SUPERUSER_CHANGE_EMAIL_DESCRIPTION =
  "Superuser emails are changed from the Admin portal.";

/** Rule 10.6.9: only Owners and Co-Owners see a pending change; the payload already omits it for Administrators. */
export function getPendingEmailChange(access, sessionPermission) {
  const pending = access?.pending_email_change;
  if (!pending?.new_email || !isOwnerOrCoOwner(sessionPermission)) return null;
  return pending;
}

export function getPendingEmailChangeLines(access, sessionPermission) {
  const pending = getPendingEmailChange(access, sessionPermission);
  if (!pending) return null;
  const expiry = formatStatusDate(pending.expires_at);
  const requester = pending.requested_by?.name;
  const secondary = [expiry && `Expires ${expiry}`, requester && `requested by ${requester}`]
    .filter(Boolean)
    .join(" · ");
  const description = [
    requester && `Requested by ${requester}.`,
    `The current address keeps working until ${pending.new_email} confirms.`,
    expiry && `Expires ${expiry}.`,
  ]
    .filter(Boolean)
    .join(" ");
  return {
    primary: `Pending email change to ${pending.new_email}`,
    secondary,
    tooltip: { title: "Pending email change", description },
  };
}

/** A Co-Owner cannot cancel a change an Owner started (Rule 10.6.9); the backend enforces it too. */
export function canCancelPendingEmailChange(access, sessionPermission, requesterPermission) {
  if (!getPendingEmailChange(access, sessionPermission)) return false;
  const isCoOwner = normalizeString(sessionPermission).replace(/[\s-]+/g, "") === "coowner";
  return !(isCoOwner && normalizeString(requesterPermission) === "owner");
}

export function buildPendingEmailChangeMenuItems(
  baseItems,
  access,
  sessionPermission,
  requesterPermission
) {
  const items = Array.isArray(baseItems) ? baseItems : [];
  if (access?.user?.is_superuser && isOwnerOrCoOwner(sessionPermission)) {
    const existing =
      items.find((item) => item.value === "change_email_action") || CHANGE_EMAIL_ITEM;
    const disabled = {
      ...existing,
      disabled: true,
      disabledReason: SUPERUSER_CHANGE_EMAIL_REASON,
      disabledDescription: SUPERUSER_CHANGE_EMAIL_DESCRIPTION,
    };
    return items.some((item) => item.value === "change_email_action")
      ? items.map((item) => (item.value === "change_email_action" ? disabled : item))
      : [...items, disabled];
  }
  if (!canCancelPendingEmailChange(access, sessionPermission, requesterPermission)) return items;
  const changeIndex = items.findIndex((item) => item.value === "change_email_action");
  if (changeIndex === -1) return [...items, CANCEL_EMAIL_CHANGE_ITEM];
  return [
    ...items.slice(0, changeIndex + 1),
    CANCEL_EMAIL_CHANGE_ITEM,
    ...items.slice(changeIndex + 1),
  ];
}
